import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    GA4EventName,
    GA4FeatureCategory,
    type GA4ItemListName,
    trackAnalyticsEvent,
    trackEecProductClick,
    trackEecProductImpression,
} from '@jsmdg/tracking';
import { Image, PureLink, Slider, SlidingDirections, StarIcon } from '@jsmdg/yoshi';
import { type Product } from '../../../../shared/dyResponse';
import { useHighestMatchingBreakpoint } from '../../../hooks/useHighestMatchingBreakpoint';
import { useImpressionTracking } from '../../../hooks/useImpressionTracking';
import { type ProductDetailContext } from '../../../types/types';
import { productSliderConfig } from './productSliderConfig';
import { SkeletonTile } from './SkeletonTile/SkeletonTile';
import styles from './ProductRecommendations.module.scss';

const messagesSlider = defineMessages({
    a11yPreviousSlide: { defaultMessage: 'Zur vorherigen Seite' },
    a11yNextSlide: { defaultMessage: 'Zur nächsten Seite' },
});

const trackSlide = (direction: string, category: string) => {
    trackAnalyticsEvent({
        category,
        action: 'Slide',
        label: direction,
    });
};

const itemListName = 'Recommendation Shopping';

type ProductSlideProps = {
    readonly position: number;
    readonly product: Product;
};

const ProductSlide = ({ position, product }: ProductSlideProps): JSX.Element => {
    const intl = useIntl();
    const [imageLoaded, setImageLoaded] = useState(false);

    const {
        discountBadge,
        images,
        manufacturerSku,
        originalPrice,
        price,
        productId,
        rating,
        title,
        trackingName,
        trackingPath,
        url,
    } = product;

    const firstImage = images?.[0];

    const payloadData = {
        product: {
            id: productId,
            url,
            trackingName,
            rating,
            manufacturerSku,
            trackingPath,
            price,
            originalPrice,
            title,
            discountBadgeId: discountBadge?.id,
            discountBadgeName: discountBadge?.text,
        },
        position,
        list: itemListName,
        featureCategory: GA4FeatureCategory.ProductDetailInformation,
        itemListName: itemListName as GA4ItemListName,
    };

    const onProductClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        event?.preventDefault();
        trackEecProductClick(payloadData);
        window.location.href = product.url;
    };

    const setImpressionTrackedElement = useImpressionTracking(() =>
        trackEecProductImpression({
            ...payloadData,
            eventName: GA4EventName.ViewItemList,
        }),
    );

    return (
        <PureLink
            href={url}
            className={classNames('d-flex flex-column w-100', styles.productTile)}
            onClick={onProductClick}
            ref={setImpressionTrackedElement as React.Ref<HTMLAnchorElement>}
        >
            <div
                className={classNames(
                    styles.productTileImageWrapper,
                    'position-relative overflow-hidden mb-1x w-100',
                )}
            >
                {firstImage && (
                    <Image
                        src={firstImage.url}
                        alt={firstImage.alt}
                        srcSet={firstImage.srcset}
                        sizes={firstImage.sizes}
                        onLoad={() => setImageLoaded(true)}
                        className={classNames(styles.productTileImage, 'w-100 h-100')}
                    />
                )}
                {!imageLoaded && (
                    <div
                        className={classNames(
                            styles.imagePlaceholer,
                            'position-absolute top-0 bottom-0 start-0 end-0',
                        )}
                    />
                )}
            </div>
            <div>
                {rating && (
                    <div
                        className={classNames(
                            styles.productTileRating,
                            'd-flex align-items-center',
                        )}
                    >
                        <StarIcon className={styles.ratingIcon} />
                        <span className={classNames(styles.ratingValue, 'fw-bold mr-0-5x')}>
                            {rating.value}
                        </span>
                        <span className={styles.ratingCount}>({rating.count})</span>
                    </div>
                )}
                <h2 className={classNames(styles.productTileTitle, ' mb-0-5x my-sm-1x p-0')}>
                    {title}
                </h2>
                <div className="d-flex flex-row mt-sm-0-5x">
                    {originalPrice && (
                        <div className={classNames(styles.productOriginalPrice, 'mr-1x')}>
                            {intl.formatNumber(Number(originalPrice.gross.amount), {
                                style: 'currency',
                                currency: originalPrice.gross.currencyCode,
                            })}
                        </div>
                    )}
                    <div
                        className={classNames(styles.productPrice, {
                            [styles.withColor]: !!originalPrice,
                        })}
                    >
                        {intl.formatNumber(Number(price.gross.amount), {
                            style: 'currency',
                            currency: price.gross.currencyCode,
                        })}
                    </div>
                </div>
            </div>
        </PureLink>
    );
};

const ProductRecommendations = (): JSX.Element | null => {
    const intl = useIntl();
    const highestMatchingBreakpoint = useHighestMatchingBreakpoint();
    const sliderConfig = productSliderConfig[highestMatchingBreakpoint];

    const { dyRecommendations } = useFragmentContext<ProductDetailContext>();

    const slides = dyRecommendations.map((product: Product, index: number) => ({
        key: product.productId,
        content: <ProductSlide product={product} position={index + 1} />,
    }));

    const skeletonSlides = Array.from({ length: sliderConfig.slidesPerView + 1 }, (_, index) => ({
        key: index.toString(),
        content: <SkeletonTile />,
    }));

    return (
        <div className={styles.sliderWrapper}>
            <Slider
                a11yNextSlide={intl.formatMessage(messagesSlider.a11yNextSlide)}
                a11yPreviousSlide={intl.formatMessage(messagesSlider.a11yPreviousSlide)}
                key="non-virtual"
                slides={!dyRecommendations.length ? skeletonSlides : slides}
                config={sliderConfig}
                handleShowNext={() => trackSlide(SlidingDirections.Next, itemListName)}
                handleShowPrevious={() => trackSlide(SlidingDirections.Previous, itemListName)}
                handleTouchStart={() => trackSlide('', itemListName)}
                handleTouchMove={() => trackSlide('', itemListName)}
                classes={{
                    swiperSlide: styles.swiperSlide,
                }}
            />
        </div>
    );
};

export { ProductRecommendations };
